import type { ErrorADT } from "pb.safe";
import type { Identity } from "pb.types";
import { v4 as uuidv4 } from "uuid";

import {
	WS_CLOSE_EVENT_CODE_1000_NORMAL_CLOSURE,
	type Payload,
	type POSConnectorMessage,
} from "../../../@lib-pos-connector-client";
import { logger } from "../../../shared/logger";

export const STATE_INITIAL_TIMEOUT = 15000; // 15 seconds
export const STATE_TRANSACTION_STATE_TIMEOUT = 40000; // 40 seconds

export function CancelRequest(sessionId: string) {
	return {
		action: "transactionEvent",
		eventId: uuidv4(),
		timestamp: Date.now().toString(),
		data: {
			sessionUuid: sessionId,
			event: "CANCEL",
		},
	} satisfies Payload;
}

/**
 * Acknowledges a terminal response so the terminal knows the result was
 * consumed. Sent from both the in-flight and cancelling response handlers.
 */
export function AcknowledgedResponse(deps: {
	action: "purchase" | "refund";
	sessionId: string;
	requestId: string;
}) {
	return {
		action: deps.action,
		eventId: uuidv4(),
		requestId: deps.requestId,
		timestamp: Date.now().toString(),
		data: {
			sessionUuid: deps.sessionId,
			state: "ACKNOWLEDGED",
		},
	} satisfies Payload;
}

/**
 * Builds the shared `closeWith*` terminators used by the refund/purchase
 * orchestrators. `resolveError` is the flow's `resolve.Error`, so the
 * flow-typed transport resolver stays per-flow.
 */
export function createCloseHandlers(deps: {
	resolveError: (
		type:
			| "Cancelled"
			| "Connection Failure"
			| "Inactivity Timeout"
			| "Network Failure"
			| "Paired Device Busy",
	) => void;
}) {
	return {
		closeWithCancelled() {
			logger.warn("pos connector: closing with cancelled");
			return void deps.resolveError("Cancelled");
		},
		closeWithConnectionFailure() {
			logger.warn("pos connector: closing with connection failure");
			return void deps.resolveError("Connection Failure");
		},
		closeWithInactivityTimeout() {
			logger.warn("pos connector: closing with inactivity timeout");
			return void deps.resolveError("Inactivity Timeout");
		},
		closeWithNetworkFailure() {
			logger.warn("pos connector: closing with network failure");
			return void deps.resolveError("Network Failure");
		},
		closeWithPairedDeviceBusy() {
			logger.warn("pos connector: closing with paired device busy");
			return void deps.resolveError("Paired Device Busy");
		},
	};
}

/**
 * Owns the reconnection policy shared by the refund/purchase orchestrators:
 * capped attempts with linear backoff, healthcheck-gated retries, and the
 * normal-closure guard. Page construction stays in-flow via `onExhausted` /
 * `onNetworkError`; `connect` re-enters the flow's own connect routine.
 * Callers `reset()` when data is successfully received.
 */
export function createReconnector(deps: {
	deviceId: string;
	healthcheck: (
		deviceId: string,
	) => Promise<undefined | ErrorADT<"NetworkError">>;
	updateTimeout: (callback: () => void, delay: number) => void;
	connect: () => void;
	onExhausted: () => void;
	onNetworkError: () => void;
}) {
	let _attempt = 0;

	function reconnect() {
		logger.warn("pos connector: reconnecting", { attempt: _attempt });

		if (_attempt >= 5) {
			logger.warn("pos connector: closing with error disconnected");
			return void deps.onExhausted();
		}

		deps.updateTimeout(async () => {
			// checks api resolution; retries with refreshed token on 401.
			// success permits web socket reconnect, failure signals network
			// error.
			const result = await deps.healthcheck(deps.deviceId);

			if (result instanceof Error) {
				return void deps.onNetworkError();
			}

			_attempt += 1;
			deps.connect();
		}, 1000 * _attempt);
	}

	function onClose(event: CloseEvent) {
		logger.debug("pos connector: on close", {
			code: event.code,
			event: event,
		});

		if (event.code === WS_CLOSE_EVENT_CODE_1000_NORMAL_CLOSURE) {
			return;
		}

		logger.error("pos connector: abnormal close", {
			eventCode: event.code,
			eventReason: event.reason,
			eventWasClean: event.wasClean,
		});

		return void reconnect();
	}

	function onError(event: Event) {
		logger.error("pos connector: on error", { event: event });
		return void reconnect();
	}

	return {
		reconnect,
		onClose,
		onError,
		reset() {
			_attempt = 0;
		},
		get attempt() {
			return _attempt;
		},
	};
}

/**
 * Builds the shared `handle()` message-error guard: transport-level errors are
 * logged and swallowed, protocol errors are mapped to the flow's callbacks
 * (`PAIRED_DEVICE_UNREACHABLE` → `onUnreachable`, `FORBIDDEN` → `onForbidden`,
 * anything else → `onOperationError`). Which page each error becomes stays
 * in-flow.
 */
export function createMessageGuard(deps: {
	onUnreachable: () => void;
	onForbidden: () => void;
	onOperationError: () => void;
}) {
	return function handle(
		callback: (message: Identity<Omit<POSConnectorMessage, "error">>) => void,
	) {
		return (message: POSConnectorMessage | Error): void => {
			if (message instanceof Error) {
				return void logger.error("pos connector: on message error", {
					errorName: message.name,
					errorMessage: message.message,
				});
			}
			if (message.error) {
				if (message.error.type === "PAIRED_DEVICE_UNREACHABLE") {
					return void deps.onUnreachable();
				}
				if (message.error.type === "FORBIDDEN") {
					return void deps.onForbidden();
				}
				return void deps.onOperationError();
			}

			return void callback(message);
		};
	};
}
